import { Box, Stack, Typography, Select, MenuItem, IconButton, Tooltip } from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useThemeColors } from "../context/ThemeContext";

// Compact page-number list with ellipses (e.g. 1 … 4 5 6 … 12), mirroring the
// DomainList footer so pagination looks identical app-wide.
function buildPageList(current, total) {
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) out.push("...");
    out.push(p);
    prev = p;
  }
  return out;
}

// Reusable pagination footer. The parent owns `page` (1-based) and `pageSize`
// state and does the slicing; this just renders the controls and reports
// changes back via onPageChange / onPageSizeChange.
//
//   <PaginationFooter
//     page={page} pageSize={pageSize} total={items.length}
//     onPageChange={setPage} onPageSizeChange={setPageSize} />
function PaginationFooter({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}) {
  const colors = useThemeColors();

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const rangeStart = total === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + pageSize, total);
  const pageList = buildPageList(safePage, totalPages);

  const navBtn = {
    color: colors.textMuted,
    "&:hover": { background: colors.cardBg, color: "#818cf8" },
  };

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ sm: "center" }}
      spacing={2}
      sx={{
        width: "100%",
        justifyContent: "space-between",
        px: 3,
        py: 2,
        borderTop: `1px solid ${colors.cardBorder}`,
        background: colors.hover,
      }}
    >
      {/* Left: range */}
      <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
        Showing{" "}
        <Box component="span" sx={{ fontWeight: 700, color: colors.text }}>
          {rangeStart}–{rangeEnd}
        </Box>{" "}
        of{" "}
        <Box component="span" sx={{ fontWeight: 700, color: colors.text }}>
          {total}
        </Box>
      </Typography>

      {/* Right: page nav + page size (selector pinned to far right) */}
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ justifyContent: { xs: "flex-start", sm: "flex-end" }, rowGap: 0.75 }}>
        {onPageSizeChange && (
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            MenuProps={{
              anchorOrigin: { vertical: "top", horizontal: "left" },
              transformOrigin: { vertical: "bottom", horizontal: "left" },
              PaperProps: {
                sx: {
                  mb: 0.5,
                  minWidth: 72,
                  borderRadius: 2,
                  background: colors.menuBg,
                  border: `1px solid ${colors.cardBorder}`,
                  boxShadow: colors.shadowLg,
                  "& .MuiList-root": { py: 0.5 },
                  "& .MuiMenuItem-root": {
                    fontSize: 13,
                    color: colors.text,
                    mx: 0.5,
                    my: 0.25,
                    minHeight: 32,
                    borderRadius: 1,
                    justifyContent: "center",
                    "&:hover": { background: colors.hover },
                    "&.Mui-selected": {
                      background: "rgba(129, 140, 248, 0.14)",
                      color: "#818cf8",
                      fontWeight: 700,
                      "&:hover": { background: "rgba(129, 140, 248, 0.2)" },
                    },
                  },
                },
              },
            }}
            sx={{
              mr: 0.5,
              height: 32,
              minWidth: 62,
              fontSize: 13,
              fontWeight: 700,
              color: colors.text,
              background: colors.cardBg,
              borderRadius: 2,
              transition: "border-color 0.15s ease, background 0.15s ease",
              "& .MuiSelect-select": {
                py: 0,
                pl: 1.5,
                pr: "28px !important",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.cardBorder },
              "&:hover": { background: colors.hover },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.cardBorderHover },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#818cf8", borderWidth: "1px" },
              "& .MuiSvgIcon-root": { color: colors.textMuted, right: 4 },
            }}
          >
            {pageSizeOptions.map((n) => (
              <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>
                {n}
              </MenuItem>
            ))}
          </Select>
        )}
        <Tooltip title="First page">
          <span>
            <IconButton size="small" disabled={safePage === 1} onClick={() => onPageChange(1)} sx={navBtn}>
              <FirstPageIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Previous">
          <span>
            <IconButton
              size="small"
              disabled={safePage === 1}
              onClick={() => onPageChange(Math.max(1, safePage - 1))}
              sx={navBtn}
            >
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>

        {pageList.map((p, i) =>
          p === "..." ? (
            <Box key={`e-${i}`} sx={{ px: 1, fontSize: 13, color: colors.textFaint }}>
              …
            </Box>
          ) : (
            <Box
              key={p}
              component="button"
              onClick={() => onPageChange(p)}
              sx={{
                minWidth: 32,
                height: 32,
                px: 1,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: p === safePage ? "#818cf8" : "transparent",
                background: p === safePage ? "rgba(79, 70, 229, 0.12)" : "transparent",
                color: p === safePage ? "#818cf8" : colors.text,
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": {
                  background: p === safePage ? "rgba(79, 70, 229, 0.18)" : colors.cardBg,
                },
              }}
            >
              {p}
            </Box>
          )
        )}

        <Tooltip title="Next">
          <span>
            <IconButton
              size="small"
              disabled={safePage === totalPages}
              onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
              sx={navBtn}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Last page">
          <span>
            <IconButton
              size="small"
              disabled={safePage === totalPages}
              onClick={() => onPageChange(totalPages)}
              sx={navBtn}
            >
              <LastPageIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

export default PaginationFooter;
